import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { AuditService } from './audit.service';
import { ConfigService } from '../shared/config';

// Route planning for the DRIVER app — see docs/driver-route-app-plan.md.
//
// Split by who is allowed to do what:
//   * Route templates are ordinary Firestore documents the admin owns, so they
//     are read and written directly through AngularFire like every other admin
//     collection.
//   * Driver accounts and run generation go through the cloud /driverAdmin
//     endpoints, because each is several coupled writes only the server can make
//     — a Firebase Auth user, a driverUsers doc and custom claims for the first,
//     a batched run + stop set for the second.
//
// `routes`, `routeRuns` and `routeStops` are ROOT collections carrying
// companyId/siteId/systemId as fields. They are deliberately flat from birth so
// they need no migration when the nesting removal in
// docs/flatten-firestore-plan.md reaches them.

export interface RouteStopTemplate {
    systemId: string;
    siteId: string | null;
    systemName?: string;
    siteName?: string;
    address?: string;
    order: number;
    note?: string;
    expectedMinutes?: number | null;
    // ISO weekdays (1=Mon … 7=Sun) this stop is served on. ABSENT means Mon–Fri
    // — see stopDueOn() in the cloud functions' driverService: an empty field
    // must not quietly schedule Saturdays. The daily materialisation honours
    // it; a manual "Generate run" takes the whole route on purpose.
    weekdays?: number[] | null;
}

export interface DeliveryRoute {
    id?: string;
    name: string;
    companyId: string;
    active: boolean;
    stops: RouteStopTemplate[];
    defaultDriverUid?: string | null;
    defaultDriverName?: string;
    // Where the vans are loaded: every round starts and ends here.
    depot?: { name: string, lat: number, lng: number } | null;
    createdAt?: number;
    createdBy?: string;
    updatedAt?: number;
    updatedBy?: string;
}

// The numbers the driver app offers under its ☰ menu. One global set, stored
// server-only (driverSettings has no rules entry) and edited via /driverAdmin.
export interface DriverSupportSettings {
    supportPhone: string;
    roadAssistPhone: string;
    extraContacts: { label: string, phone: string }[];
}

export interface DriverAccount {
    uid: string;
    email: string;
    name: string;
    phone: string;
    employer: string;
    role: string;
    companyIds: string[];
    // Panel features the driver may use (claim `features`); only 'delivery' exists.
    features?: string[];
    status: boolean;
    // A panel user flagged Driver who has no driver profile yet — shown in the
    // list so a manager can add phone, employer and companies.
    profileMissing?: boolean;
    createdAt?: number;
    createdBy?: string;
}

@Injectable({ providedIn: 'root' })
export class DeliveryService {

    private base = environment.apiUrl + '/widgets/driverAdmin';

    // Cached so createdBy/updatedBy are synchronous at the call site. Mirrors
    // the pattern in AuditService.
    private actorEmail = 'unknown';

    constructor(
        private afs: AngularFirestore,
        private http: HttpClient,
        private auth: AuthService,
        private audit: AuditService,
        private config: ConfigService
    ) {
        this.auth.user$.subscribe(user => {
            if (user) this.actorEmail = (user as any).email || 'unknown';
        });
    }

    private async authOptions() {
        const token = await this.auth.getToken();
        return {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token
            })
        };
    }

    // ---- companies ----------------------------------------------------------

    // A driver may work for more than one company — a route can span them
    // (Bergen 1 is Frigg Vest while the rest are Frigg Øst), and subcontractors
    // are shared. So the Drivers screen picks companies explicitly rather than
    // silently inheriting whichever one the dashboard happens to have selected.
    companies$(): Observable<{ id: string, name: string }[]> {
        return this.afs.collection('company').snapshotChanges().pipe(
            map(docs => docs
                .map(d => {
                    const data = d.payload.doc.data() as any;
                    return { id: d.payload.doc.id, name: data.name || data.companyName || d.payload.doc.id };
                })
                .sort((a, b) => a.name.localeCompare(b.name)))
        );
    }

    // ---- kantiner available to put on a route -------------------------------

    // Deliberately NOT StockingServiceService.getSystems$(), which filters
    // `active == true`. Measured in production 2026-08-11: of 97 system_info
    // docs only 74 have that flag set — 19 are explicitly false and 4 do not
    // carry the field at all. Filtering on it hides 7 of Frigg's own 66 systems
    // and empties the picker completely for six smaller companies, which reads
    // as "this feature is broken" rather than "these are switched off".
    //
    // So: fetch every system for the company and let the caller show the state.
    // An operator planning a route is entitled to see a kantine that is
    // currently off — that is often exactly why it needs a visit.
    systems$(): Observable<any[]> {
        return this.afs
            .collection('system_info', ref => ref.where('companyId', '==', this.config.getCompanyId()))
            .snapshotChanges()
            .pipe(map(docs => docs
                .map(d => {
                    const data = d.payload.doc.data() as any;
                    return {
                        id: d.payload.doc.id,
                        ...data,
                        // `active` missing is not the same as false, but for
                        // display both mean "not currently serving".
                        isActive: data.active === true
                    };
                })
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            ));
    }

    // ---- route templates ----------------------------------------------------

    routes$(): Observable<DeliveryRoute[]> {
        return this.afs
            .collection('routes', ref => ref.where('companyId', '==', this.config.getCompanyId()))
            .snapshotChanges()
            .pipe(map(docs => docs
                .map(d => ({ id: d.payload.doc.id, ...(d.payload.doc.data() as any) } as DeliveryRoute))
                // Sorted here rather than in Firestore so a route with no name
                // yet (just created) does not need a composite index.
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            ));
    }

    route$(routeId: string): Observable<DeliveryRoute | null> {
        return this.afs.collection('routes').doc(routeId).snapshotChanges().pipe(
            map(d => d.payload.exists ? ({ id: d.payload.id, ...(d.payload.data() as any) } as DeliveryRoute) : null)
        );
    }

    createRoute(name: string) {
        return this.afs.collection('routes').add({
            name,
            companyId: this.config.getCompanyId(),
            active: true,
            stops: [],
            defaultDriverUid: null,
            defaultDriverName: '',
            createdAt: moment().unix(),
            createdBy: this.actorEmail,
            updatedAt: moment().unix(),
            updatedBy: this.actorEmail,
            ...this.audit.stamp('create')
        });
    }

    // The whole stops array is written at once. A route has tens of stops, is
    // read whole and edited by one person at a time, so per-stop writes would
    // buy nothing and cost a partial-order bug.
    saveRoute(routeId: string, patch: Partial<DeliveryRoute>) {
        return this.afs.collection('routes').doc(routeId).update({
            ...patch,
            updatedAt: moment().unix(),
            ...this.audit.stamp('update')
        });
    }

    async deleteRoute(routeId: string, reason: string | null = null) {
        // Stamp before deleting: the audit trigger reads `_audit` off the
        // document as it was last written, so an unstamped delete is anonymous.
        await this.afs.collection('routes').doc(routeId).update(this.audit.stamp('delete', reason));
        return this.afs.collection('routes').doc(routeId).delete();
    }

    // ---- runs ---------------------------------------------------------------

    // Runs are read directly (admin dashboard, operator token) but only ever
    // WRITTEN by the server, so a half-generated day cannot exist.
    runsForDate$(date: string): Observable<any[]> {
        return this.afs
            .collection('routeRuns', ref => ref
                .where('companyId', '==', this.config.getCompanyId())
                .where('date', '==', date))
            .snapshotChanges()
            .pipe(map(docs => docs.map(d => ({ id: d.payload.doc.id, ...(d.payload.doc.data() as any) }))));
    }

    // The stops of one run, live, in driving order — the dispatch board's feed.
    // routeStops is operator-readable (server-write-only), so this is a plain
    // query; `order` is a stored field, so no composite index is needed beyond
    // (runId, order) merging single-field indexes... sorted in memory anyway.
    stopsForRun$(runId: string): Observable<any[]> {
        return this.afs
            .collection('routeStops', ref => ref.where('runId', '==', runId))
            .snapshotChanges()
            .pipe(map(docs => docs
                .map(d => ({ id: d.payload.doc.id, ...(d.payload.doc.data() as any) }))
                .sort((a, b) => (a.order || 0) - (b.order || 0))));
    }

    async generateRun(routeId: string, date: string, driverUid: string | null): Promise<any> {
        const opts = await this.authOptions();
        return this.http.post(this.base + '/runs',
            JSON.stringify({ routeId, date, driverUid }), opts).toPromise();
    }

    async getRun(runId: string): Promise<any> {
        const opts = await this.authOptions();
        return this.http.get(this.base + '/runs/' + encodeURIComponent(runId), opts).toPromise();
    }

    // ---- "how to find the machine" guides -----------------------------------

    // One doc per system in root `systemGuides`: floor, directions, entrance
    // pin and photos. Operators edit here; drivers get it in their bundle and
    // add photos through /driver. Photos live in Storage under
    // systemGuides/{systemId}/.
    async getGuide(systemId: string): Promise<any | null> {
        const snap = await this.afs.collection('systemGuides').doc(systemId).ref.get();
        return snap.exists ? snap.data() : null;
    }

    // Map pins for a set of systems, in one place, for the dispatch map. The
    // van's destination is what an operator wants to see, so parking wins over
    // the entrance door, and a cached geocode is the last resort. A system with
    // no guide at all is simply absent from the result — the caller says so out
    // loud rather than drawing nothing and letting it look like a bug.
    async getGuidePoints(systemIds: string[]): Promise<{ [systemId: string]: { lat: number, lng: number, source: 'parking' | 'entrance' | 'geo' } }> {
        const ids = Array.from(new Set((systemIds || []).filter(id => !!id)));
        const out: { [systemId: string]: { lat: number, lng: number, source: 'parking' | 'entrance' | 'geo' } } = {};
        const pt = (p: any) => p && typeof p.lat === 'number' && typeof p.lng === 'number'
            ? { lat: p.lat, lng: p.lng } : null;
        await Promise.all(ids.map(async id => {
            try {
                const snap = await this.afs.collection('systemGuides').doc(id).ref.get();
                if (!snap.exists) return;
                const g: any = snap.data() || {};
                const parking = pt(g.parking), entrance = pt(g.entrance), geo = pt(g.geo);
                if (parking) out[id] = { ...parking, source: 'parking' };
                else if (entrance) out[id] = { ...entrance, source: 'entrance' };
                else if (geo) out[id] = { ...geo, source: 'geo' };
            } catch {
                // One unreadable guide must not empty the whole map.
            }
        }));
        return out;
    }

    saveGuide(systemId: string, data: any) {
        return this.afs.collection('systemGuides').doc(systemId).set({
            ...data,
            systemId,
            updatedAt: moment().unix(),
            updatedBy: this.actorEmail
        }, { merge: true });
    }

    // ---- driver last-seen ---------------------------------------------------

    // Last known position per driver uid, written by /driver. The dispatch
    // board shows it as "last seen"; positions only flow while the driver has
    // the app open, and the app says so to the driver.
    driverLocation$(driverUid: string): Observable<any | null> {
        return this.afs.collection('driverLocations').doc(driverUid).snapshotChanges().pipe(
            map(d => d.payload.exists ? d.payload.data() : null)
        );
    }

    // ---- issue triage -------------------------------------------------------

    // Problems reported from the field (channelIssues, source 'driver'). Two
    // equality filters only — this project's composite indexes are created by
    // hand, so the sort happens in memory.
    issues$(status: 'open' | 'resolved'): Observable<any[]> {
        return this.afs
            .collection('channelIssues', ref => ref
                .where('companyId', '==', this.config.getCompanyId())
                .where('status', '==', status))
            .snapshotChanges()
            .pipe(map(docs => docs
                .map(d => ({ id: d.payload.doc.id, ...(d.payload.doc.data() as any) }))
                .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))));
    }

    resolveIssue(issueId: string, resolution: string | null = null) {
        return this.afs.collection('channelIssues').doc(issueId).update({
            status: 'resolved',
            resolvedAt: moment().unix(),
            resolvedBy: this.actorEmail,
            resolution: resolution || null
        });
    }

    reopenIssue(issueId: string) {
        return this.afs.collection('channelIssues').doc(issueId).update({
            status: 'open', resolvedAt: null, resolvedBy: null
        });
    }

    // ---- support numbers ----------------------------------------------------

    async getDriverSettings(): Promise<DriverSupportSettings> {
        const opts = await this.authOptions();
        const res: any = await this.http.get(this.base + '/settings', opts).toPromise();
        return res?.settings || { supportPhone: '', roadAssistPhone: '', extraContacts: [] };
    }

    async saveDriverSettings(settings: DriverSupportSettings): Promise<DriverSupportSettings> {
        const opts = await this.authOptions();
        const res: any = await this.http.put(this.base + '/settings', JSON.stringify(settings), opts).toPromise();
        return res?.settings;
    }

    // ---- driver accounts ----------------------------------------------------

    async listDrivers(companyId?: string): Promise<DriverAccount[]> {
        const opts = await this.authOptions();
        const url = this.base + '/users' + (companyId ? '?companyId=' + encodeURIComponent(companyId) : '');
        const res: any = await this.http.get(url, opts).toPromise();
        const users: DriverAccount[] = (res && res.users) || [];
        // The name people know a driver by is the one on their admin user
        // account (users/{uid}.Name), not whatever the driver profile holds.
        try {
            const snap = await this.afs.collection('users', ref => ref.where('roles.driver', '==', true))
                .get().toPromise();
            const names: { [uid: string]: string } = {};
            snap.forEach(d => { names[d.id] = ((d.data() as any) || {}).Name || ''; });
            users.forEach(u => { if (names[u.uid]) u.name = names[u.uid]; });
        } catch (err) {
            // Keep the profile names rather than failing the whole list.
        }
        return users;
    }

    async createDriver(payload: {
        email: string, name: string, phone: string, employer: string,
        role: string, companyIds: string[], features?: string[]
    }): Promise<any> {
        const opts = await this.authOptions();
        return this.http.post(this.base + '/users', JSON.stringify(payload), opts).toPromise();
    }

    async updateDriver(uid: string, patch: any): Promise<any> {
        const opts = await this.authOptions();
        return this.http.patch(this.base + '/users/' + uid, JSON.stringify(patch), opts).toPromise();
    }

    async resendInvite(uid: string): Promise<any> {
        const opts = await this.authOptions();
        return this.http.post(this.base + '/users/' + uid + '/resendInvite', '{}', opts).toPromise();
    }
}
